import { TeamInfo } from '../TeamInfo';
import { RaceRules } from '../types';
import { gapToSeconds, parseGap } from './gap';
import { parseLapTimeSeconds, stintMinutes } from './lapTime';
import { fieldMedianPitLoss, medianSeconds, TeamPitHistory } from './pitStops';

export interface StrategyInputs {
  teams: Map<string, TeamInfo>;
  pitHistory: Map<string, TeamPitHistory>;
  rules: RaceRules;
  /** Absolute session end, epoch ms; null before Apex sends a countdown. */
  sessionEndsAt: number | null;
  /** Injected so a replay projects from its virtual clock, not wall time. */
  now: number;
  /** Teams treated as "ours" — their stops define our pit loss. */
  ourTeamIds?: Set<string>;
}

export interface TeamStrategy {
  teamId: string;
  kartNumber: string;
  label: string;
  /** Representative pace in seconds; null until enough laps are in. */
  paceSeconds: number | null;
  /** Stint age in minutes; null while boxed. */
  stintMinutes: number | null;
  /** Minutes before the stint cap bites. Negative means already past it. */
  stintRemainingMinutes: number | null;
  /** Whether this stint can reach the flag without boxing again. */
  canRunToFinish: boolean;
  /** Further stops needed to reach the flag legally; null if unknowable. */
  stopsRemaining: number | null;
  /** Gap to the leader now, in seconds. Zero for the leader. */
  gapSeconds: number | null;
  /** Gap to the leader projected forward to the flag. */
  projectedGapSeconds: number | null;
  currentPosition: number | null;
  projectedPosition: number | null;
  /** Positive means projected to gain places. */
  positionDelta: number | null;
}

export interface StrategySummary {
  /** Typical stationary time to plan with. */
  pitLossSeconds: number | null;
  /** Whether that came from our own stops or the field's. */
  pitLossSource: 'team' | 'field' | null;
  minutesRemaining: number | null;
  teams: TeamStrategy[];
}

/**
 * Best available pace estimate, in order of preference: the outlier-trimmed
 * clean average, then the 10- and 5-lap rolling averages, then the best lap.
 * Best lap is a poor last resort — it flatters a team — but it beats nothing
 * when a kart has only just come out.
 */
export function paceOf(team: TeamInfo): number | null {
  const candidates = [
    team.getCleanAverageLap(),
    team.getAverageLapTime10(),
    team.getAverageLapTime(),
    team.bestLap,
  ];
  for (const candidate of candidates) {
    const seconds = parseLapTimeSeconds(candidate);
    if (seconds !== null && seconds > 0) return seconds;
  }
  return null;
}

function positionOf(team: TeamInfo): number | null {
  const n = parseInt(team.position, 10);
  return Number.isFinite(n) ? n : null;
}

function labelOf(team: TeamInfo): string {
  const name = team.teamName?.trim() ?? '';
  const driver = team.driver?.trim() ?? '';
  if (name && driver && name.toLowerCase() !== driver.toLowerCase()) {
    return `${name} (${driver})`;
  }
  return name || driver || `Kart ${team.kartNumber || '—'}`;
}

/**
 * Stops still required to reach the flag.
 *
 * A boxed kart is about to start a fresh stint, so it gets the full cap; a kart
 * on track only has what is left of its current one. `requiredStops` raises the
 * answer when the regulations demand more stops than the stint cap alone forces.
 */
export function stopsRemainingFor(
  team: TeamInfo,
  rules: RaceRules,
  minutesRemaining: number | null,
): number | null {
  if (minutesRemaining === null) return null;

  const age = stintMinutes(team.onTrackTime);
  const runwayMinutes = team.inBox
    ? rules.maxStintMinutes
    : age === null
      ? null
      : rules.maxStintMinutes - age;

  let stops: number | null = null;
  if (runwayMinutes !== null) {
    const afterThisStint = minutesRemaining - Math.max(runwayMinutes, 0);
    stops = afterThisStint <= 0
      ? 0
      : Math.ceil(afterThisStint / rules.maxStintMinutes);
  }

  if (rules.requiredStops !== null) {
    const made = parseInt(team.pitCount, 10);
    const outstanding = Math.max(
      0,
      rules.requiredStops - (Number.isFinite(made) ? made : 0),
    );
    stops = stops === null ? outstanding : Math.max(stops, outstanding);
  }

  return stops;
}

/**
 * Project each team's gap to the leader forward to the flag.
 *
 * Working in gaps rather than absolute times sidesteps a problem with lap
 * counts: the backend often starts recording mid-race, so `lapCount` is laps
 * *observed*, not laps completed, and cannot be compared across teams.
 */
export function computeStrategy(inputs: StrategyInputs): StrategySummary {
  const { teams, pitHistory, rules, sessionEndsAt, now, ourTeamIds } = inputs;

  const minutesRemaining =
    sessionEndsAt === null ? null : Math.max(0, (sessionEndsAt - now) / 60_000);

  // Our own stops describe our own pit lane; fall back to the field's median
  // until we have made one.
  const ourStops: number[] = [];
  if (ourTeamIds) {
    ourTeamIds.forEach((id) => {
      const history = pitHistory.get(id);
      if (history) ourStops.push(...history.completed);
    });
  }
  const ourMedian = medianSeconds(ourStops);
  const fieldMedian = fieldMedianPitLoss(pitHistory);
  const pitLossSeconds = ourMedian ?? fieldMedian;
  const pitLossSource: StrategySummary['pitLossSource'] =
    ourMedian !== null ? 'team' : fieldMedian !== null ? 'field' : null;

  const rows: TeamStrategy[] = [];
  teams.forEach((team, teamId) => {
    const paceSeconds = paceOf(team);
    const age = stintMinutes(team.onTrackTime);
    const stintRemainingMinutes =
      age === null ? null : rules.maxStintMinutes - age;
    const stopsRemaining = stopsRemainingFor(team, rules, minutesRemaining);

    rows.push({
      teamId,
      kartNumber: team.kartNumber,
      label: labelOf(team),
      paceSeconds,
      stintMinutes: age,
      stintRemainingMinutes,
      canRunToFinish: stopsRemaining === 0,
      stopsRemaining,
      gapSeconds: gapToSeconds(parseGap(team.gap), paceSeconds),
      projectedGapSeconds: null,
      currentPosition: positionOf(team),
      projectedPosition: null,
      positionDelta: null,
    });
  });

  const leader = rows.reduce<TeamStrategy | null>((best, row) => {
    if (row.currentPosition === null) return best;
    if (best?.currentPosition == null) return row;
    return row.currentPosition < best.currentPosition ? row : best;
  }, null);

  // The leader's own gap is zero, whatever Apex left in the cell.
  if (leader) leader.gapSeconds = 0;

  const leaderPace = leader?.paceSeconds ?? null;
  const leaderStops = leader?.stopsRemaining ?? null;

  if (leaderPace !== null && minutesRemaining !== null) {
    for (const row of rows) {
      if (row.paceSeconds === null || row.gapSeconds === null) continue;

      // How many laps the leader still has to run — the horizon over which a
      // pace difference accumulates.
      const lapsRemaining = (minutesRemaining * 60) / leaderPace;
      const paceDrift = (row.paceSeconds - leaderPace) * lapsRemaining;

      let stopDrift = 0;
      if (
        pitLossSeconds !== null &&
        row.stopsRemaining !== null &&
        leaderStops !== null
      ) {
        stopDrift = (row.stopsRemaining - leaderStops) * pitLossSeconds;
      }

      row.projectedGapSeconds = row.gapSeconds + paceDrift + stopDrift;
    }

    const projected = rows
      .filter((r) => r.projectedGapSeconds !== null)
      .sort((a, b) => a.projectedGapSeconds! - b.projectedGapSeconds!);

    projected.forEach((row, index) => {
      row.projectedPosition = index + 1;
      row.positionDelta =
        row.currentPosition === null
          ? null
          : row.currentPosition - row.projectedPosition;
    });
  }

  rows.sort((a, b) => {
    const ap = a.currentPosition ?? Number.MAX_SAFE_INTEGER;
    const bp = b.currentPosition ?? Number.MAX_SAFE_INTEGER;
    return ap - bp;
  });

  return { pitLossSeconds, pitLossSource, minutesRemaining, teams: rows };
}

export interface PitNowOutcome {
  /** Where we would rejoin, by current running order. */
  rejoinPosition: number | null;
  /** Karts we would come out behind and ahead of. */
  behind: TeamStrategy | null;
  ahead: TeamStrategy | null;
  /** Seconds we would lose relative to staying out. */
  lossSeconds: number | null;
}

/**
 * Where a stop right now would drop us. Everyone within `pitLoss` seconds behind
 * us comes past, so the answer is how far down the gap ladder that reaches.
 */
export function simulatePitNow(
  summary: StrategySummary,
  teamId: string,
): PitNowOutcome {
  const { pitLossSeconds } = summary;
  const ladder = summary.teams
    .filter((t) => t.gapSeconds !== null)
    .sort((a, b) => a.gapSeconds! - b.gapSeconds!);

  const self = ladder.find((t) => t.teamId === teamId) ?? null;
  if (!self || pitLossSeconds === null) {
    return { rejoinPosition: null, behind: null, ahead: null, lossSeconds: null };
  }

  const rejoinGap = self.gapSeconds! + pitLossSeconds;
  const others = ladder.filter((t) => t.teamId !== teamId);

  // Rejoin behind everyone whose gap is smaller than ours-after-the-stop.
  const passedCount = others.filter((t) => t.gapSeconds! < rejoinGap).length;

  return {
    rejoinPosition: passedCount + 1,
    behind: others[passedCount - 1] ?? null,
    ahead: others[passedCount] ?? null,
    lossSeconds: pitLossSeconds,
  };
}
